import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  increment,
  onSnapshot,
  setDoc,
  collection,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Modal from "react-modal";

dayjs.extend(relativeTime);

Modal.setAppElement("#root");

const InvestorCard = ({ investor, visitorMode = false, showThumbnail = true, compact = false }) => {
  const navigate = useNavigate();
  const [lastSeen, setLastSeen] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [isAvailableForDeals, setIsAvailableForDeals] = useState(false);
  const [profileViews, setProfileViews] = useState(0);

  // Live lists
  const [endorsementsList, setEndorsementsList] = useState([]);
  const [complaintsList, setComplaintsList] = useState([]);

  // Permissions and state
  const [canReview, setCanReview] = useState(false);
  const [hasEndorsed, setHasEndorsed] = useState(false);
  const [hasComplained, setHasComplained] = useState(false);

  // Review UI state
  const [endorseText, setEndorseText] = useState("");
  const [complaintText, setComplaintText] = useState("");
  const [modalType, setModalType] = useState(null); // 'endorse' or 'complaint' for list modal
  const [activePopover, setActivePopover] = useState(null); // 'endorse' | 'complaint' | null
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const endorseBtnRef = useRef(null);
  const complaintBtnRef = useRef(null);
  const popoverRef = useRef(null);

  const {
    firstName,
    fullName,
    industry,
    location,
    investmentRange,
    bio,
    profileImages = [],
    endorsements = [],
    complaints = [],
    boostActive,
    boostExpiresAt,
    boostedUntil,
    verified,
    goldTier,
    id,
  } = investor;

  // Normalize images: dedupe, cap to 10
  const uniqueImages = Array.from(new Set((profileImages || []).filter(Boolean)));
  const displayImages = uniqueImages.slice(0, 10);
  const thumbImage = displayImages[0];
  const otherImages = displayImages.slice(1);

  const isBoostActive = (user) => {
    if (!user) return false;
    if (user.boostActive && user.boostExpiresAt) {
      try {
        const expiry = user.boostExpiresAt.toDate ? user.boostExpiresAt.toDate() : new Date(user.boostExpiresAt);
        return expiry > new Date();
      } catch (e) {}
    }
    if (user.boostedUntil) {
      try {
        const expiry = user.boostedUntil.seconds ? new Date(user.boostedUntil.seconds * 1000) : new Date(user.boostedUntil);
        return expiry > new Date();
      } catch (e) {}
    }
    return false;
  };

  // Live status
  useEffect(() => {
    if (!id) return;
    const unsubscribe = onSnapshot(doc(db, "status", id), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsOnline(data.isOnline || false);
        setLastSeen(data.lastSeen || null);
      }
    });
    return () => unsubscribe();
  }, [id]);

  // Live availability + views
  useEffect(() => {
    if (!id) return;
    const unsubscribe = onSnapshot(doc(db, "users", id), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsAvailableForDeals(data.isAvailableForDeals || false);
        setProfileViews(data.profileViews || 0);
      }
    });
    return () => unsubscribe();
  }, [id]);

  // Real-time endorsements & complaints
  useEffect(() => {
    if (!id) return;
    const endUnsub = onSnapshot(
      query(collection(db, "endorsements"), where("investorId", "==", id)),
      (snap) => setEndorsementsList(snap.docs.map((d) => d.data()))
    );
    const compUnsub = onSnapshot(
      query(collection(db, "complaints"), where("investorId", "==", id)),
      (snap) => setComplaintsList(snap.docs.map((d) => d.data()))
    );
    return () => {
      endUnsub();
      compUnsub();
    };
  }, [id]);

  // Can review & already endorsed/complained
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!id || !currentUser || currentUser.uid === id) {
      setCanReview(false);
      setHasEndorsed(false);
      setHasComplained(false);
      return;
    }
    getDoc(doc(db, "contacts", `${currentUser.uid}_${id}`)).then((snap) => setCanReview(snap.exists()));
    getDoc(doc(db, "endorsements", `${id}_${currentUser.uid}`)).then((snap) => setHasEndorsed(snap.exists()));
    getDoc(doc(db, "complaints", `${id}_${currentUser.uid}`)).then((snap) => setHasComplained(snap.exists()));
  }, [id]);

  const handleClick = async () => {
    if (visitorMode) return navigate("/login");
    const currentUser = auth.currentUser;
    if (!currentUser) return navigate("/login");
    if (currentUser.uid === id) return;

    const userRef = doc(db, "users", currentUser.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return alert("⚠️ User data not found.");

    const userData = userSnap.data();
    const contactsRemaining = userData.contactsRemaining || 0;

    if (contactsRemaining > 0) {
      await updateDoc(userRef, { contactsRemaining: contactsRemaining - 1 });
      const investorRef = doc(db, "users", id);
      await updateDoc(investorRef, { profileViews: increment(1) });
      setProfileViews((prev) => prev + 1);
      const contactRef = doc(db, "contacts", `${currentUser.uid}_${id}`);
      await setDoc(
        contactRef,
        { founderId: currentUser.uid, investorId: id, startedAt: new Date() },
        { merge: true }
      );
      navigate(`/inbox?user=${id}`);
    } else {
      alert("⚠️ You have 0 Investor Contacts Remaining. Please upgrade your plan.");
      navigate("/pricing");
    }
  };

  const openPopover = (type) => {
    const ref = type === "endorse" ? endorseBtnRef : complaintBtnRef;
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + 8, left: Math.min(rect.left, window.innerWidth - 320) });
      setActivePopover(type);
    }
  };

  useEffect(() => {
    if (!activePopover) return;
    const handler = (e) => {
      if (
        (popoverRef.current && popoverRef.current.contains(e.target)) ||
        (endorseBtnRef.current && endorseBtnRef.current.contains(e.target)) ||
        (complaintBtnRef.current && complaintBtnRef.current.contains(e.target))
      ) {
        return;
      }
      setActivePopover(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [activePopover]);

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return "🕒 Last seen: Not available";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return `🕒 Last seen: ${dayjs(date).fromNow()}`;
  };

  const handleSubmitEndorse = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !canReview || hasEndorsed || hasComplained) return;
    const comment = endorseText.trim();
    if (!comment) return;
    await setDoc(
      doc(db, "endorsements", `${id}_${currentUser.uid}`),
      {
        investorId: id,
        founderId: currentUser.uid,
        founderName: currentUser.displayName || "Anonymous",
        comment,
        createdAt: new Date(),
      },
      { merge: true }
    );
    setHasEndorsed(true);
    setActivePopover(null);
    setEndorseText("");
  };

  const handleSubmitComplaint = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !canReview || hasEndorsed || hasComplained) return;
    const comment = complaintText.trim();
    if (!comment) return;
    await setDoc(
      doc(db, "complaints", `${id}_${currentUser.uid}`),
      {
        investorId: id,
        founderId: currentUser.uid,
        founderName: currentUser.displayName || "Anonymous",
        comment,
        createdAt: new Date(),
      },
      { merge: true }
    );
    setHasComplained(true);
    setActivePopover(null);
    setComplaintText("");
  };

  const eList = endorsementsList.length > 0 ? endorsementsList : endorsements || [];
  const cList = complaintsList.length > 0 ? complaintsList : complaints || [];
  const endorseCount = eList.length;
  const complaintCount = cList.length;
  const hasReviewed = hasEndorsed || hasComplained;

  const renderComments = (list, limit = 3) => (list.length <= limit ? list : list.slice(0, limit));
  const renderCommentItem = (item, idx) => {
    const created = item?.createdAt?.toDate ? item.createdAt.toDate() : item?.createdAt || null;
    return (
      <div
        key={idx}
        className="text-xs p-2 rounded-lg flex-shrink-0 min-w-[120px] bg-gray-50 border border-gray-100"
      >
        <strong>{item?.founderName || "Anonymous"}</strong>: {item?.comment}{" "}
        <span className="text-gray-400 text-[10px]">
          ({created ? dayjs(created).fromNow() : ""})
        </span>
      </div>
    );
  };

  const boosted = isBoostActive(investor);

  const getThumbUrl = () => {
    if (thumbImage) return thumbImage;
    const name = encodeURIComponent(firstName || fullName || "Investor");
    return `https://ui-avatars.com/api/?name=${name}&background=random&size=100`;
  };

  return (
    <div className="p-4 border rounded-3xl shadow-lg bg-white w-full max-w-3xl mx-auto mb-6 hover:shadow-2xl transition duration-300 relative">
      {/* Floating badges (top-left) */}
      <div className="absolute -top-4 left-4 flex flex-col gap-1 z-20">
        {boosted && (
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: [1, 1.03, 1] }}
            transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2 }}
            className="bg-gradient-to-r from-orange-400 to-red-400 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg"
            style={{ boxShadow: "0 10px 30px rgba(249,115,22,0.18)" }}
            title="🔥 Boosted"
          >
            🔥 Boosted
          </motion.span>
        )}

        {!boosted && goldTier && (
          <span
            className="bg-yellow-100 text-yellow-700 text-xs font-bold px-3 py-1 rounded-full shadow-sm shimmer-badge"
            title="🌟 Gold-tier Investor"
          >
            🌟 Gold-tier
          </span>
        )}
      </div>

      {/* Profile Section */}
      <div className="flex justify-center items-start gap-4 mb-4 flex-wrap">
        {/* Left: Endorsements */}
        <div
          className={`flex flex-col items-start flex-1 min-w-[120px] max-w-[180px] ${
            compact ? "hidden" : ""
          }`}
        >
          <h3 className="font-semibold text-pink-600 text-sm text-center mb-1 flex items-center gap-2">
            <span>❤️ Endorsements</span>
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pink-100 text-pink-700 text-[10px]">
              {endorseCount}
            </span>
          </h3>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide py-1">
            {renderComments(eList).map((e, idx) => renderCommentItem(e, idx))}
            {eList.length > 3 && (
              <div
                className="text-xs text-pink-600 cursor-pointer"
                onClick={() => setModalType("endorse")}
              >
                + see more
              </div>
            )}
          </div>
        </div>

        {/* Profile Image (forced circular mask with fallback) + gold verified pill to the left */}
        {showThumbnail && (
          <div className="relative inline-flex items-center gap-2 flex-nowrap whitespace-nowrap align-middle">
            {verified && (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold text-yellow-900 bg-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 border border-yellow-500 shadow-sm shrink-0"
                title="★ Verified Investor"
              >
                ★ Verified
              </span>
            )}
            <div className="relative shrink-0">
              <div
                className="rounded-full overflow-hidden shadow-md border border-gray-200 hover:scale-105 hover:shadow-xl transition-all duration-300"
                style={{ width: "100px", height: "100px", borderRadius: "50%", clipPath: "circle(50% at 50% 50%)" }}
              >
                <img
                  src={getThumbUrl()}
                  alt="Investor Thumbnail"
                  className="w-full h-full object-cover"
                  style={{ borderRadius: "50%", display: "block" }}
                />
              </div>
              <span
                className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white ${
                  isOnline ? "bg-green-500 animate-pulse" : "bg-red-500"
                }`}
              ></span>
            </div>
          </div>
        )}

        {/* Right: Complaints */}
        <div
          className={`flex flex-col items-end flex-1 min-w-[120px] max-w-[180px] ${
            compact ? "hidden" : ""
          }`}
        >
          <h3 className="font-semibold text-red-600 text-sm text-center mb-1 flex items-center gap-2">
            <span>💢 Complaints</span>
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-[10px]">
              {complaintCount}
            </span>
          </h3>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide py-1">
            {renderComments(cList).map((c, idx) => renderCommentItem(c, idx))}
            {cList.length > 3 && (
              <div
                className="text-xs text-red-600 cursor-pointer"
                onClick={() => setModalType("complaint")}
              >
                + see more
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image gallery: remaining images as 100x100 circles (no overlap) */}
      {otherImages.length > 0 && (
        <div className="rounded-xl mb-4 mt-2 flex flex-wrap justify-center gap-3">
          {otherImages.map((imgUrl, index) => (
            <img
              key={index}
              src={imgUrl}
              alt={`Investor ${index + 2}`}
              className="rounded-full object-cover border border-gray-200 shadow-sm"
              style={{ width: "100px", height: "100px", borderRadius: "50%", clipPath: "circle(50% at 50% 50%)" }}
            />
          ))}
        </div>
      )}

      {/* Investor Info */}
      <div className="mt-2 space-y-1 text-center">
        <h2 className="text-xl font-bold flex justify-center items-center gap-2">
          {firstName || fullName}
        </h2>
        <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
          {isOnline ? (
            <span className="text-green-600 font-semibold">🟢 Online now</span>
          ) : (
            <span>{formatLastSeen(lastSeen)}</span>
          )}
        </p>
        <p>
          📍 <strong>Location:</strong> {location || "Not specified"}
        </p>
        <p>
          💼 <strong>Industry:</strong> {industry || "Not specified"}
        </p>
        <p>
          💰 <strong>Investment Range:</strong> {investmentRange || "N/A"}
        </p>
        <p>
          📝 <strong>Bio:</strong> {bio || "No bio provided"}
        </p>
        <p className="mt-2">
          {isAvailableForDeals ? (
            <span className="text-green-600 font-semibold">
              🟢 I am looking for a deal right now.
            </span>
          ) : (
            <span className="text-red-600 font-semibold">
              🔴 I am not looking for a deal at the moment.
            </span>
          )}
        </p>
        <p className="mt-2">👁 {profileViews} Profile Views</p>
      </div>

      {/* Contact Button */}
      <div className="mt-4 text-center">
        {visitorMode ? (
          <button
            onClick={handleClick}
            className="px-5 py-2 rounded-full text-white font-semibold bg-blue-500 hover:bg-blue-600 transition"
          >
            💬 Login to Contact
          </button>
        ) : isAvailableForDeals ? (
          <button
            onClick={handleClick}
            className="px-5 py-2 rounded-full text-white font-semibold bg-blue-500 hover:bg-blue-600 transition"
          >
            💬 Contact Investor
          </button>
        ) : (
          <div className="px-5 py-2 rounded-full font-semibold bg-gray-300 text-gray-600 cursor-not-allowed">
            ⚠️ Not looking for a deal at the moment.
          </div>
        )}
      </div>

      {/* Action strip: endorse/complain gating */}
      <div className="mt-3 flex justify-center">
        {!canReview && (
          <div className="text-xs text-gray-500">
            Contact the investor to endorse or complain.
          </div>
        )}
        {canReview && !hasReviewed && (
          <div className="flex gap-3">
            <button
              ref={endorseBtnRef}
              onClick={() => openPopover("endorse")}
              className="px-3 py-1.5 rounded-full bg-pink-600 text-white text-xs hover:bg-pink-700 transition"
            >
              ❤️ Endorse
            </button>
            <button
              ref={complaintBtnRef}
              onClick={() => openPopover("complaint")}
              className="px-3 py-1.5 rounded-full bg-red-600 text-white text-xs hover:bg-red-700 transition"
            >
              💢 Complain
            </button>
          </div>
        )}
        {canReview && hasReviewed && (
          <div className="text-xs text-gray-600">
            You have already submitted feedback.
          </div>
        )}
      </div>

      {/* Modal for all endorsements & complaints */}
      <Modal
        isOpen={modalType !== null}
        onRequestClose={() => setModalType(null)}
        className="bg-white p-4 rounded-2xl max-w-lg mx-auto mt-20 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start z-50"
      >
        <h3 className="font-bold text-lg mb-4">
          {modalType === "endorse" ? "All Endorsements" : "All Complaints"}
        </h3>
        <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
          {(modalType === "endorse" ? eList : cList).map((item, idx) => renderCommentItem(item, idx))}
        </div>
        <button
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-full"
          onClick={() => setModalType(null)}
        >
          Close
        </button>
      </Modal>

     
     
      {/* Inline review popover */}
      {activePopover && (
        <div
          ref={popoverRef}
          className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-[300px]"
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <h3 className="font-semibold text-sm mb-2">
            {activePopover === "endorse" ? "❤️ Add Endorsement" : "💢 Add Complaint"}
          </h3>
          <textarea
            className="w-full border rounded-lg p-2 text-sm"
            rows={3}
            placeholder={activePopover === "endorse" ? "Write a short endorsement..." : "Write a short complaint..."}
            value={activePopover === "endorse" ? endorseText : complaintText}
            onChange={(e) => (activePopover === "endorse" ? setEndorseText(e.target.value) : setComplaintText(e.target.value))}
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              className="px-3 py-1.5 rounded-full bg-gray-200 text-gray-700 text-sm"
              onClick={() => setActivePopover(null)}
            >
              Cancel
            </button>
            <button
              className={`px-3 py-1.5 rounded-full text-white text-sm ${activePopover === "endorse" ? "bg-pink-600 hover:bg-pink-700" : "bg-red-600 hover:bg-red-700"} disabled:opacity-50`}
              onClick={activePopover === "endorse" ? handleSubmitEndorse : handleSubmitComplaint}
              disabled={!canReview || hasReviewed || !(activePopover === "endorse" ? endorseText.trim() : complaintText.trim())}
            >
              Submit
            </button>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        @keyframes gold-shimmer {
          0% { text-shadow: 0 0 2px #FFD700, 0 0 5px #FFD700; }
          50% { text-shadow: 0 0 6px #FFD700, 0 0 12px #FFD700; }
          100% { text-shadow: 0 0 2px #FFD700, 0 0 5px #FFD700; }
        }
        .shimmer-badge { animation: gold-shimmer 2s infinite; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default InvestorCard;